import { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import useInterview from "../../utils/hooks/useInterview";
import NewSideBar from "../../components/Navigation/NewSideBar";
import { LayoutContext } from "../../utils/contexts/LayoutContext";
import Select from "../../components/forms/Select/Select";
import {
  getQuestionTypes,
  getSkills,
  postQuestion,
} from "../../services/Interviews/questionService";
import { getInterview } from "../../services/Interviews/interviewService";

const CreateQuestionPage = () => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const { setPadding } = useContext(LayoutContext);
  useEffect(() => {
    setPadding("p-0");
    return () => {
      setPadding("p-4");
    };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const questionCount = location.state?.questionCount || 0;
  const [title, setTitle] = useState("");
  const [questionTypes, setQuestionTypes] = useState([]);
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    setTitle(`Question ${questionCount + 1}`);
  }, [questionCount]);

  const { postquestions } = useInterview();

  const fetchData = useCallback(async (signal) => {
    try {
      const questionTypesResponse = await getQuestionTypes({ signal });
      const skillsResponse = await getSkills({ signal });
      const interviewData = location.state?.interviewUuid
        ? await getInterview(location.state.interviewUuid, { signal })
        : null;

      console.log("questionTypesResponse", questionTypesResponse);
      console.log("skillsResponse", skillsResponse);
      setQuestionTypes(questionTypesResponse.data.results);
      const allSkills = skillsResponse.data.results;
      const allowed = new Set(interviewData?.skills?.map((skill) => skill.uuid) || []);
      setSkills(interviewData ? allSkills.filter((skill) => allowed.has(skill.uuid)) : allSkills);
    } catch (error) {
      console.error("Fetch Failed", error);
    }
  }, [location.state?.interviewUuid]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const selectedTypeUuid = watch("type");
  const selectedType = questionTypes?.find(
    (type) => type.uuid === selectedTypeUuid
  );
  const isDynamicFollowup = selectedType?.code === "DYNAMIC_FOLLOWUP";

  const onSubmit = async (data) => {
    try {
      await postQuestion(data);
      console.log("Post Success");
      console.log(data);
      navigate(location.state?.returnTo || "/question/question-bank");
    } catch (error) {
      console.log(data);
      console.error("Post Failed", error);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-300">
      <div className="flex h-full">
        <NewSideBar />
        <form
          className="flex flex-col w-screen"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex-1 p-8 bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
            <button
              className="text-sm text-gray-600 dark:text-gray-300 py-5 hover:underline"
              onClick={() => navigate(location.state?.returnTo || "/question/question-bank")}
            >
              ← Back
            </button>

            <h2 className="text-2xl font-bold mb-1">{title}</h2>
            <div className="flex flex-col mb-4">
              <Controller
                name="name"
                control={control}
                defaultValue=""
                rules={{ required: "Question Title is required" }}
                render={({ field }) => (
                  <input
                    {...field}
                    className="text-m border rounded text-gray-500 dark:bg-gray-800 dark:text-white dark:border-gray-600"
                    type="text"
                    placeholder="Give your question a title."
                  />
                )}
              />
              {errors.name && (
                <p className="text-red-500 text-xs">{errors.name.message}</p>
              )}
            </div>

            <div className="mb-4">
              <Controller
                name="type"
                control={control}
                defaultValue=""
                rules={{ required: "Question Type is required" }}
                render={({ field }) => (
                  <Select
                    label="Question Type"
                    placeholder="Select question type"
                    options={
                      questionTypes?.map((type) => ({
                        value: type.uuid,
                        label: type.name,
                      })) || []
                    }
                    value={field.value}
                    onChange={(selected) => field.onChange(selected?.value)}
                    required
                    className="mb-2"
                  />
                )}
              />
              {errors.type && (
                <p className="text-red-500 text-xs">{errors.type.message}</p>
              )}
            </div>

            {isDynamicFollowup && (
              <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                Follow-up questions are adaptive: the AI keeps probing until it
                has enough substance, within your organization's follow-up
                limits (see Organization settings).
              </p>
            )}

            <div className="mb-4">
              <Controller
                name="skills"
                control={control}
                defaultValue={[]}
                rules={{ required: "At least one skill is required" }}
                render={({ field }) => (
                  <Select
                    label="Skills"
                    placeholder="Select skills"
                    options={
                      skills?.map((skill) => ({
                        value: skill.uuid,
                        label: skill.name,
                      })) || []
                    }
                    value={field.value}
                    onChange={(selected) =>
                      field.onChange(selected?.map((s) => s.value) || [])
                    }
                    isMulti
                    required
                    className="mb-2"
                  />
                )}
              />
              {errors.skills && (
                <p className="text-red-500 text-xs">{errors.skills.message}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block font-semibold mb-1">
                Question Content
              </label>
              <div>
                <Controller
                  name="body"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Question Content is required" }}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      type="text"
                      placeholder="Write your question here"
                      rows={4}
                      className="
                      w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white dark:border-gray-600"
                    />
                  )}
                />
                {errors.body && (
                  <p className="text-red-500 text-xs">{errors.body.message}</p>
                )}
              </div>
            </div>

            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2"></div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Add to Bank
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateQuestionPage;
